<template>
    <basic-header
        pretitle="aqfer Universal Tag"
        title="Tag Template Library"
        buttonText="Quick Add Tag Template"
        :buttonRoles= "btnRoles"
        @btnClick="showModal({ component: 'addTagTemplate' })"
        buttonText2="Tag Template Wizard"
        @btnClick2="showModal({ component: 'addTagTemplateWizard', data: { dialogClass: 'modal-wizard' } })"
        buttonClass2="btn-primary"
        :button2Roles="btnRoles"

    />

    <div class="container-fluid">
        <div class="card">
            <div class="filter-bar">
                <form class="filter-fields" @submit.prevent="applyFilters">
                    <div class="filter-field">
                        <label class="filter-label" for="tt-filter-category">Category</label>
                        <input id="tt-filter-category" class="form-control form-control-sm" type="text"
                            list="tt-filter-category-options" autocomplete="off"
                            placeholder="Any" v-model="draft.category" />
                        <datalist id="tt-filter-category-options">
                            <option v-for="tag in knownTags" :key="tag" :value="tag"></option>
                        </datalist>
                    </div>

                    <div class="filter-field">
                        <label class="filter-label" for="tt-filter-date">Created on or after</label>
                        <input id="tt-filter-date" class="form-control form-control-sm" type="date"
                            v-model="draft.date" />
                    </div>

                    <div class="filter-field filter-field-expression">
                        <label class="filter-label" for="tt-filter-parameter">Field</label>
                        <div class="input-group input-group-sm">
                            <input id="tt-filter-parameter" class="form-control"
                                type="text" placeholder="tid" autocomplete="off"
                                v-model="draft.fieldParameter" />
                            <span class="input-group-text filter-operator">=</span>
                            <input class="form-control" type="text"
                                placeholder="farmer" autocomplete="off"
                                aria-label="Field value" v-model="draft.fieldValue" />
                        </div>
                    </div>

                    <div class="filter-actions">
                        <button class="btn btn-sm btn-primary" type="submit" :disabled="!isDirty">
                            Apply
                        </button>
                        <button v-if="hasAppliedFilters || isDirty" class="btn btn-sm btn-link filter-reset"
                            type="button" @click="clearFilters">
                            Reset
                        </button>
                    </div>
                </form>

                <transition name="filter-chips">
                    <div v-if="activeChips.length" class="filter-chips">
                        <span class="filter-chips-label">Filtered by</span>
                        <button v-for="chip in activeChips" :key="chip.key" type="button"
                            class="filter-chip" @click="removeChip(chip.key)"
                            :aria-label="`Remove filter ${chip.label} ${chip.value}`">
                            <span class="filter-chip-label">{{ chip.label }}</span>
                            <span class="filter-chip-value">{{ chip.value }}</span>
                            <span class="filter-chip-remove" aria-hidden="true">&times;</span>
                        </button>
                        <span class="filter-count">{{ resultSummary }}</span>
                    </div>
                </transition>
            </div>

            <datatable
                ref="tagTable"
                tableId="tagList"
                :columns="columns"
                :formatColumn="formatColumn"
                :data="$store.state.aut.tagTemplates"
                @deleteRows="showDeleteTagTemplates"
                :deletable="deletable"
                :compact="true"
                :selectCheckboxes="true"
                :multiselectColumn="multiselectColumn"
                uniqueField="name"
                :detailColumn="{baseUrl:'tag-templates/', name:'Tag Template', identifier:'name',identifierName:'name'}"
                :customTitle="copyable ? 'Copy' : null"
                customClass="btn btn-outline-primary"
                customExtend="selectedSingle"
                customTooltip="Select a single tag template to copy it under a new name."
                @customActionButton="showCopyTagTemplate"
            />
        </div>
    </div>
</template>

<script>
import BasicHeader from "../../components/BasicHeader.vue";
import Datatable from "../../components/Datatable.vue";
import { mapActions, mapMutations, mapState } from "vuex";

const EMPTY_FILTERS = { category: "", date: "", fieldParameter: "", fieldValue: "" };

export default {
    name: "Tags",
    components: { BasicHeader, Datatable },
    data() {
        return {
            columns: [
                { data: "name" },
                { data: "event_type", title: "Event Type" },
                { data: "initiator" },
                { data: "tag_type", title: "Tag Type" },
                { data: "tag", title: "Tags" },
                { data: "owner" },
                { data: "created_at", title: "Created" },
                { data: "modified_at", title: "Updated" },
            ],
            draft: { ...EMPTY_FILTERS },
            applied: { ...EMPTY_FILTERS },
            totalCount: null,
            knownTags: [],
            btnRoles: [1, 1.5, 3],
            formatColumn:[
                {
                    index:8,
                    type:"datetime"
                },
                {
                    index:9,
                    type:"datetime"
                }
            ],
            multiselectColumn:[ 6 ]
        };
    },
    beforeMount() {
        this.applyFilters();
    },
    methods: {
        applyFilters() {
            this.applied = { ...this.draft };
            return this.fetchTagTemplates({
                category: this.applied.category.trim(),
                date: this.applied.date ? this.applied.date.replaceAll("-", "") : "",
                fields: this.applied.fieldParameter.trim()
                    ? { [this.applied.fieldParameter.trim()]: this.applied.fieldValue.trim() }
                    : {},
            }).catch(() => {
            }).then(() => {
                if (this.hasAppliedFilters) return;
                const templates = this.$store.state.aut.tagTemplates || [];
                this.totalCount = templates.length;
                this.knownTags = [...new Set(
                    templates
                        .flatMap((template) => (template.tag || "").split(","))
                        .map((tag) => tag.trim())
                        .filter(Boolean)
                )].sort();
            });
        },
        clearFilters() {
            this.draft = { ...EMPTY_FILTERS };
            this.applyFilters();
        },
        removeChip(key) {
            if (key === "field") {
                this.draft.fieldParameter = "";
                this.draft.fieldValue = "";
            } else {
                this.draft[key] = "";
            }
            this.applyFilters();
        },
        showDeleteTagTemplates(rows) {
            this.showModal({ component: "deleteTagTemplates", data: rows });
        },
        showCopyTagTemplate() {
            //Note: the button uses the "selectedSingle" extend, so there is always exactly one row.
            const rows = this.$refs.tagTable.getSelectedRows();
            if (!rows.length) return;
            this.showModal({ component: "copyTagTemplate", data: rows[0] });
        },
        ...mapActions({
            showModal: "showModal",
            fetchTagTemplates: "aut/fetchTagTemplates",
        }),
    },
    computed: {
        isDirty() {
            return Object.keys(EMPTY_FILTERS)
                .some((key) => this.draft[key].trim() !== this.applied[key].trim());
        },
        hasAppliedFilters() {
            return Object.keys(EMPTY_FILTERS).some((key) => this.applied[key].trim());
        },
        activeChips() {
            const chips = [];
            if (this.applied.category.trim()) {
                chips.push({ key: "category", label: "Category", value: this.applied.category.trim() });
            }
            if (this.applied.date) {
                chips.push({ key: "date", label: "Created on or after", value: this.formattedDate });
            }
            if (this.applied.fieldParameter.trim()) {
                chips.push({
                    key: "field",
                    label: this.applied.fieldParameter.trim(),
                    value: `= ${this.applied.fieldValue.trim() || '""'}`,
                });
            }
            return chips;
        },
        formattedDate() {
            const [year, month, day] = this.applied.date.split("-").map(Number);
            return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
                year: "numeric", month: "short", day: "numeric", timeZone: "UTC",
            });
        },
        resultSummary() {
            const shown = (this.$store.state.aut.tagTemplates || []).length;
            const noun = (count) => (count === 1 ? "template" : "templates");
            if (this.totalCount === null || this.totalCount === shown) {
                return `${shown} ${noun(shown)}`;
            }
            return `${shown} of ${this.totalCount} ${noun(this.totalCount)}`;
        },
        deletable() {
            const allowedRoles = [1, 1.5, 3];
            return  (allowedRoles.includes(this.$store.getters.userRoles.userRoleRank));
        },
        copyable() {
            //Note: copying creates a template, so it needs the same roles as adding one.
            return this.$store.getters.userRoles.isAccessByRole(this.btnRoles);
        }
    }

};
</script>

<style scoped>
.filter-bar {
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid var(--bs-border-color, #edf2f9);
}

.filter-fields {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.75rem 1.25rem;
}

.filter-field {
    display: flex;
    flex-direction: column;
    gap: 0.3125rem;
    min-width: 11rem;
}

.filter-field-expression {
    min-width: 17rem;
    flex: 1 1 17rem;
    max-width: 26rem;
}

.filter-label {
    margin: 0;
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--bs-gray-600, #95aac9);
}

.filter-operator {
    padding-inline: 0.5rem;
    font-weight: 600;
    color: var(--bs-gray-600, #95aac9);
    background-color: transparent;
}

.filter-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-left: auto;
}

.filter-reset {
    padding-inline: 0.5rem;
    text-decoration: none;
    color: var(--bs-gray-600, #95aac9);
}

.filter-reset:hover {
    color: var(--bs-primary, #2c7be5);
}

.filter-chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.875rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--bs-border-color, #edf2f9);
}

.filter-chips-label {
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--bs-gray-600, #95aac9);
}

.filter-chip {
    display: inline-flex;
    align-items: baseline;
    gap: 0.375rem;
    padding: 0.1875rem 0.5rem;
    border: 1px solid transparent;
    border-radius: 999px;
    background-color: rgba(44, 123, 229, 0.08);
    color: var(--bs-primary, #2c7be5);
    font-size: 0.8125rem;
    line-height: 1.4;
    transition: background-color 0.12s ease, border-color 0.12s ease;
}

.filter-chip:hover,
.filter-chip:focus-visible {
    background-color: rgba(44, 123, 229, 0.14);
    border-color: rgba(44, 123, 229, 0.35);
}

.filter-chip-label {
    opacity: 0.75;
}

.filter-chip-value {
    font-weight: 600;
}

.filter-chip-remove {
    font-size: 1rem;
    line-height: 1;
    opacity: 0.55;
}

.filter-chip:hover .filter-chip-remove {
    opacity: 1;
}

.filter-count {
    margin-left: auto;
    font-size: 0.8125rem;
    color: var(--bs-gray-600, #95aac9);
    font-variant-numeric: tabular-nums;
}

.filter-chips-enter-active,
.filter-chips-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.filter-chips-enter-from,
.filter-chips-leave-to {
    opacity: 0;
    transform: translateY(-0.25rem);
}

body.dark-mode .filter-bar,
body.dark-mode .filter-chips {
    border-color: rgba(255, 255, 255, 0.08);
}

body.dark-mode .filter-chip {
    background-color: rgba(108, 162, 232, 0.16);
    color: #6ca2e8;
}

body.dark-mode .filter-chip:hover,
body.dark-mode .filter-chip:focus-visible {
    background-color: rgba(108, 162, 232, 0.26);
    border-color: rgba(108, 162, 232, 0.4);
}

@media (max-width: 767.98px) {
    .filter-bar {
        padding: 1rem;
    }

    .filter-field,
    .filter-field-expression {
        min-width: 100%;
        max-width: none;
    }

    .filter-actions {
        margin-left: 0;
    }

    .filter-count {
        margin-left: 0;
        width: 100%;
    }
}
</style>
